import type { LoggerService } from "@/services/abstract/LoggerService";
import type { MessageHandlerResult } from "@/services/abstract/poc/MessagingService";
import type { MessageHandler, MessagePayload } from "@/types/poc/message";

function isMessageOfType<Type extends MessagePayload['type']>(
  message: MessagePayload,
  type: Type
): message is Extract<MessagePayload, { type: Type }> {
  return message.type === type;
}

/**
 * Builds a message handler for a single discriminated-union variant: matches on
 * `message.type`, guards/narrows once, logs receipt through the injected
 * `LoggerService`, and maps the payload to its result via `toResult`.
 *
 * Centralizes the `canHandle` + type-check + `{ type, data }` skeleton that was
 * copy-pasted across the per-message handlers, and replaces the module-level
 * `createLogger` shim with the DI-provided logger.
 */
export function makeMessageHandler<Type extends MessageHandlerResult['type']>(
  type: Type,
  namespace: string,
  loggerService: LoggerService,
  toResult: (
    message: Extract<MessagePayload, { type: Type }>
  ) => Extract<MessageHandlerResult, { type: Type }>
): MessageHandler<Extract<MessageHandlerResult, { type: Type }>> {
  const log = loggerService.createLogger({ namespace });

  return {
    canHandle: (_topic: string, message: MessagePayload): boolean =>
      message.type === type,

    process: (message: MessagePayload): Extract<MessageHandlerResult, { type: Type }> => {
      log.info(`Received ${type} from`, message.sender);
      if (!isMessageOfType(message, type)) {
        throw new Error(`Invalid message type: expected '${type}', got '${message.type}'`);
      }
      return toResult(message);
    },
  };
}
